import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

export interface Fragrance {
  id: number;
  user_id: number;
  house: string;
  name: string;
  concentration?: string;
  size_oz?: number;
  size_ml?: number;
  notes_top?: string;
  notes_middle?: string;
  notes_base?: string;
  is_clone: boolean;
  original_fragrance?: string;
  winter_rating?: number;
  spring_rating?: number;
  summer_rating?: number;
  fall_rating?: number;
  longevity?: number;
  sillage?: number;
  day_rating?: number;
  night_rating?: number;
  overall_rating?: number;
  personal_notes?: string;
  is_favorite: boolean;
  is_decant: boolean;
  created_at: string;
  updated_at: string;
}

interface FragranceCardProps {
  fragrance: Fragrance;
  onEdit?: (fragrance: Fragrance) => void;
  onDelete?: (fragranceId: number) => void;
  onToggleFavorite?: (fragranceId: number) => void;
  onCopyDetails?: (fragrance: Fragrance) => void;
}

const FragranceCard: React.FC<FragranceCardProps> = ({
  fragrance,
  onEdit,
  onDelete,
  onToggleFavorite,
  onCopyDetails
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const { user } = useAuth();

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.username === 'Demo') return;
    onToggleFavorite?.(fragrance.id);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.username === 'Demo') return;
    onEdit?.(fragrance);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.username === 'Demo') return;
    if (window.confirm(`Are you sure you want to delete "${fragrance.name}" by ${fragrance.house}?`)) {
      onDelete?.(fragrance.id);
    }
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopyDetails?.(fragrance);
  };

  const getConcentrationDisplay = () => {
    const concentrationMap: Record<string, string> = {
      'EdC': 'Eau de Cologne',
      'EdT': 'Eau de Toilette',
      'EdP': 'Eau de Parfum',
      'Parfum': 'Parfum',
      'Elixir': 'Elixir',
      'Extrait': 'Extrait de Parfum'
    };
    return fragrance.concentration ? concentrationMap[fragrance.concentration] || fragrance.concentration : '';
  };

  const getSizeDisplay = () => {
    const sizes = [];
    if (fragrance.size_oz) sizes.push(`${fragrance.size_oz} oz`);
    if (fragrance.size_ml) sizes.push(`${fragrance.size_ml} ml`);
    return sizes.join(' / ');
  };

  const getLongevityLabel = (rating?: number) => {
    const labels = ['', 'Very Weak', 'Weak', 'Moderate', 'Long Lasting', 'Eternal'];
    return rating ? labels[rating] : '';
  };

  const getSillageLabel = (rating?: number) => {
    const labels = ['', 'Intimate', 'Moderate', 'Strong', 'Enormous'];
    return rating ? labels[rating] : '';
  };

  const renderStarRatingSmall = (rating?: number, maxRating: number = 5) => {
    if (rating == null || rating === 0) return null;
  
    const numericRating = Number(rating);
  
    return (
      <div className="flex items-center space-x-0.5">
        {Array.from({ length: maxRating }, (_, index) => {
          const diff = numericRating - index;
          let fillPercent = 0;
  
          if (diff >= 1) fillPercent = 100;
          else if (diff > 0) {
            if (diff <= 0.25) fillPercent = 30;
            else if (diff <= 0.5) fillPercent = 45;
            else if (diff <= 0.75) fillPercent = 55;
            else fillPercent = 100;
          }
  
          return (
            <div key={index} className="relative w-3.5 h-3.5 flex-shrink-0">
              {/* Empty star outline */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                className="w-full h-full text-gray-300 dark:text-gray-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 .587l3.668 7.568L24 9.75l-6 5.847L19.335 24 12 19.897 4.665 24 6 15.597 0 9.75l8.332-1.595L12 .587z"
                />
              </svg>
  
              {/* Yellow filled portion */}
              {fillPercent > 0 && (
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{ clipPath: `inset(0 ${100 - fillPercent}% 0 0)` }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-full h-full text-yellow-400"
                  >
                    <path d="M12 .587l3.668 7.568L24 9.75l-6 5.847L19.335 24 12 19.897 4.665 24 6 15.597 0 9.75l8.332-1.595L12 .587z" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const houseTrimmed = fragrance.house?.trim().toLowerCase();
  const montagneHouses = [
    "montagne",
    "montagneparfum",
    "montagneparfums",
    "montagne parfum",
    "montagne parfums",
    "montagne perfume",
    "montagne perfumes",
  ];
  const isMontagne = montagneHouses.includes(houseTrimmed);

  return (
    <motion.div
      className="card-flip w-full h-[28rem] cursor-pointer"
      onClick={handleFlip}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <div className={`card-flip-inner ${isFlipped ? 'transform rotateY-180' : ''}`} style={{ transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>
        {/* Front of card */}
        <div className="card-flip-front bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {fragrance.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {fragrance.house}
                </p>
              </div>
               <div className="flex items-center space-x-2 ml-2">
                 {(fragrance.is_decant === true || (fragrance.is_decant as any) === 1) && (
                   <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                     Decant
                   </span>
                 )}
                 {(fragrance.is_clone === true || (fragrance.is_clone as any) === 1) && (
                   <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full">
                     Clone
                   </span>
                 )}
                <button
                  onClick={handleFavoriteClick}
                  className={`text-xl ${
                    fragrance.is_favorite 
                      ? 'text-red-500 hover:text-red-600' 
                      : 'text-gray-300 dark:text-gray-600 hover:text-red-500'
                  } transition-colors duration-200`}
                >
                  ♥
                </button>
              </div>
            </div>

            {/* Key Details */}
            <div className="flex-1 space-y-3">
              {fragrance.concentration && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Concentration
                  </span>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {getConcentrationDisplay()}
                  </p>
                </div>
              )}

              {getSizeDisplay() && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Size
                  </span>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {getSizeDisplay()}
                  </p>
                </div>
              )}

               {fragrance.overall_rating && fragrance.overall_rating > 0 && (
                 <div>
                   <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                     Overall Rating
                   </span>
                   <div className="flex items-center space-x-2">
                     {renderStarRatingSmall(fragrance.overall_rating, 10)}
                     <span className="text-sm font-medium text-gray-900 dark:text-white">
                       {fragrance.overall_rating}/10
                     </span>
                   </div>
                 </div>
               )}

               {/* Performance Preview */}
               {((fragrance.longevity && fragrance.longevity > 0) || (fragrance.sillage && fragrance.sillage > 0)) && (
                 <div>
                   <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                     Performance
                   </span>
                   <div className="flex items-center space-x-4 text-xs">
                     {fragrance.longevity && fragrance.longevity > 0 && (
                       <span className="text-gray-600 dark:text-gray-400">
                         Longevity: {getLongevityLabel(fragrance.longevity)}
                       </span>
                     )}
                     {fragrance.sillage && fragrance.sillage > 0 && (
                       <span className="text-gray-600 dark:text-gray-400">
                         Sillage: {getSillageLabel(fragrance.sillage)}
                       </span>
                     )}
                   </div>
                 </div>
               )}

               {/* Quick Notes Preview */}
               {fragrance.notes_top && (
                 <div>
                   <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                     Top Notes
                   </span>
                   <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-normal break-words line-clamp-2">
                     {fragrance.notes_top}
                   </p>
                 </div>
               )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Click to flip for details
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                
                    const baseUrl = isMontagne
                      ? "https://www.parfumo.com/s_perfumes_x.php?in=1&filter="
                      : "https://www.fragrantica.com/search/?query=";
                
                    const query = isMontagne
                      ? fragrance.name.replace(/\s+/g, "+")
                      : encodeURIComponent(fragrance.name);
                
                    window.open(`${baseUrl}${query}`, "_blank");
                  }}
                    className="text-gray-400 hover:text-purple-500 transition-colors duration-200"
                    title={isMontagne ? "Search on Parfumo" : "Search on Fragrantica"}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z"
                      />
                    </svg>
                </button>
                <button
                  onClick={handleEditClick}
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={handleCopyClick}
                  className="text-gray-400 hover:text-green-500 transition-colors duration-200"
                  title="Copy Details"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Back of card */}
        <div className="card-flip-back bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {fragrance.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {fragrance.house}
                </p>
              </div>
            </div>

            {/* Detailed Information */}
             <div className="flex-1 space-y-4 overflow-y-auto custom-scrollbar pr-3">
              {/* Notes */}
              {(fragrance.notes_top || fragrance.notes_middle || fragrance.notes_base) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Notes</h4>
                  <div className="space-y-1 text-xs">
                    {fragrance.notes_top && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Top:</span>
                        <span className="ml-1 text-gray-600 dark:text-gray-400">{fragrance.notes_top}</span>
                      </div>
                    )}
                    {fragrance.notes_middle && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Middle:</span>
                        <span className="ml-1 text-gray-600 dark:text-gray-400">{fragrance.notes_middle}</span>
                      </div>
                    )}
                    {fragrance.notes_base && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Base:</span>
                        <span className="ml-1 text-gray-600 dark:text-gray-400">{fragrance.notes_base}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Performance */}
              <div className="grid grid-cols-2 gap-4">
                {fragrance.longevity && (
                  <div className="text-center">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                      Longevity
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {getLongevityLabel(fragrance.longevity)}
                    </p>
                  </div>
                )}

                {fragrance.sillage && (
                  <div className="text-center">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                      Sillage
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {getSillageLabel(fragrance.sillage)}
                    </p>
                  </div>
                )}
              </div>

              {/* Seasonal Ratings */}
              {(fragrance.winter_rating || fragrance.spring_rating || fragrance.summer_rating || fragrance.fall_rating) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 text-center">Seasonal Ratings</h4>
                   <div className="grid grid-cols-2 gap-2 text-xs">
                     {fragrance.winter_rating && (
                       <div className="flex items-center justify-between">
                         <span className="text-gray-600 dark:text-gray-400">❄️ Winter:</span>
                         <div className="flex items-center">
                           {renderStarRatingSmall(fragrance.winter_rating, 5)}
                         </div>
                       </div>
                     )}
                    {fragrance.spring_rating && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">🌸 Spring:</span>
                        <div className="flex items-center">
                          {renderStarRatingSmall(fragrance.spring_rating, 5)}
                        </div>
                      </div>
                    )}
                    {fragrance.summer_rating && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">☀️ Summer:</span>
                        <div className="flex items-center">
                          {renderStarRatingSmall(fragrance.summer_rating, 5)}
                        </div>
                      </div>
                    )}
                    {fragrance.fall_rating && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">🍂 Fall:</span>
                        <div className="flex items-center">
                          {renderStarRatingSmall(fragrance.fall_rating, 5)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Day/Night Ratings */}
              {(fragrance.day_rating || fragrance.night_rating) && (
                <div className="grid grid-cols-2 gap-4">
                  {fragrance.day_rating && (
                    <div className="text-center">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                        ☀️ Day Rating
                      </span>
                       <div className="flex justify-center">
                         {renderStarRatingSmall(fragrance.day_rating, 5)}
                       </div>
                     </div>
                   )}
                   {fragrance.night_rating && (
                     <div className="text-center">
                       <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                         🌙 Night Rating
                       </span>
                       <div className="flex justify-center">
                         {renderStarRatingSmall(fragrance.night_rating, 5)}
                       </div>
                    </div>
                  )}
                </div>
              )}

              {/* Personal Notes */}
              {fragrance.personal_notes && fragrance.personal_notes.trim() && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Personal Notes</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {fragrance.personal_notes}
                  </p>
                </div>
              )}

              {/* Clone Information */}
              {(fragrance.is_clone === true || (fragrance.is_clone as any) === 1) && fragrance.original_fragrance && fragrance.original_fragrance.trim() && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Original Fragrance</h4>
                  <p
                    className="flex items-center text-sm text-gray-600 dark:text-gray-400 cursor-pointer underline underline-offset-2 decoration-dotted hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        `https://www.fragrantica.com/search/?query=${encodeURIComponent(fragrance.original_fragrance ?? "")}`,
                        "_blank"
                      );
                    }}
                    title="Search on Fragrantica"
                  >
                    {fragrance.original_fragrance}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3.5 h-3.5 ml-1 opacity-70"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18 13v6a1 1 0 01-1 1H7a1 1 0 01-1-1V7a1 1 0 011-1h6"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 3h6v6m0-6L10 14"
                      />
                    </svg>
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-center pt-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Click to flip back
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FragranceCard;
